import { OdataParams, OdataService } from '@methylium/odata';
import { DevicesService, Device as SdkDevice } from '@rocksoft/mdm-sdk-v2';
import { Device } from '@rocksoft/shared';
import { IDatasource, IGetRowsParams } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export class DevicesOverviewDatasource implements IDatasource {
  public currentOdataModel: OdataParams;

  private datasource: Device[] = [];
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly api: DevicesService,
    private readonly odataService: OdataService
  ) {}

  getRows(params: IGetRowsParams): void {
    const newOdataModel: OdataParams = {
      top: params.endRow - params.startRow,
      skip: params.startRow,
      filter: this.odataService.generateFilterStringForApi(params.filterModel) || undefined,
    };

    if (this.shouldResetData(newOdataModel)) {
      this.datasource = [];
    }

    this.currentOdataModel = newOdataModel;
    let lastRow = -1;

    if (this.shouldFetchData(params)) {
      // Item range not available in datasource. Fetch it from the API

      const devicesSub = this.getDevices(this.currentOdataModel)
        .pipe(filter((devices: Device[]) => devices !== null))
        .subscribe((devices: Device[]) => {
          this.datasource = this.datasource.concat(devices);

          lastRow = this.datasource.length < params.endRow ? this.datasource.length : -1;

          params.successCallback(devices, lastRow);
        });

      this.subscriptions.push(devicesSub);
    } else {
      // Item range available in datasource

      lastRow = this.datasource.length < params.endRow ? this.datasource.length : -1;
      const currentData = this.datasource.slice(params.startRow, params.endRow);

      params.successCallback(currentData, lastRow);
    }
  }

  private getDevices(odataParams: OdataParams): Observable<Device[]> {
    return this.api
      .getDevices({
        apiVersion: '2.0',
        $orderby: odataParams.orderby,
        $filter: odataParams.filter,
        $top: odataParams.top,
        $skip: odataParams.skip,
        $skiptoken: odataParams.skiptoken,
        $select: odataParams.select,
        $expand: odataParams.expand,
      })
      .pipe(
        map((devices: SdkDevice[]) =>
          devices.map(
            (device): Device => ({
              udid: device.identifier,
              name: device.displayName,
              serialNumber: device.serialNumber,
              lastSeen: device.lastSeen,
              platform: device.type,
              hasLoadedMdmDevice: true,
            })
          )
        )
      );
  }

  private shouldFetchData(params: IGetRowsParams): boolean {
    return this.datasource.length <= params.endRow && this.datasource.length <= params.startRow;
  }

  private shouldResetData(newOdataModel: OdataParams) {
    return this.currentOdataModel?.filter !== newOdataModel.filter;
  }

  destroy() {
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
    this.datasource = [];
  }
}
